import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowLeft, Xmark } from '@gravity-ui/icons';
import { Button, Icon, Modal } from '@gravity-ui/uikit';
import { Step, StepLabel, Stepper } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';
import _ from 'lodash';

import { useNavigateReplace, useSafeParams } from 'hooks';
import { useGetBlankQuery, useUpsertBlankMutation } from 'store/api';
import { clearStepData, markDataAsSubmitted, setStepData } from 'store/slices/blank-slice';
import type { AppState } from 'store/store';
import { getLatestByDateAndId } from 'shared/utils/get-latest-by-date-and-id';
import type { Blank, PatientEstimate, Problem } from 'store/types/common';
import { routes } from 'routes';

import type { StepOneValidationSchema } from './step-one-form/utils/use-validation';
import type { StepThreeInfo } from './step-three-form/utils/use-validation';
import type { StepTwoInfo } from './step-two-form/utils/use-validation';
import {
	GENDER_MAPPING,
	RETURN_BTN,
	RETURN_QUESTION,
	RETURN_STAY,
	RETURN_TEXT,
	SPOKSMAN_MAPPING,
	TITLE,
} from './constants';
import { StepOneForm } from './step-one-form';
import { StepThreeForm } from './step-three-form';
import { StepTwoForm } from './step-two-form';

import styles from './styles.module.scss';
import { useMediaQuery } from 'react-responsive';
import { toast } from 'react-toastify';

const steps = [
	'Шаг 1. Информация о клиенте',
	'Шаг 2. Определение проблем и оценка важности',
	'Шаг 3. Оценка выполнения и удовлетворенности',
];

export const NewBlank = () => {
	const [activeStep, setActiveStep] = useState(0);
	const [open, setOpen] = useState(false);

	const isMobile = useMediaQuery({ query: '(max-width: 515px)' });

	const { clientId } = useSafeParams();

	const [localClientId, setLocalClientId] = useState<number | null>(null);

	const dispatch = useDispatch();
	const [upsertBlank] = useUpsertBlankMutation();
	const navigate = useNavigateReplace();

	const stepData = useSelector((state: AppState) => state.blankState.stepData);
	const currentUser = useSelector((state: AppState) => state.userState.user);

	const isDataSubmitted = useSelector((state: AppState) => state.blankState.isDataSubmitted);

	const {
		data: blankData,
		isSuccess,
		refetch,
	} = useGetBlankQuery(localClientId ?? clientId ?? skipToken, {
		refetchOnMountOrArgChange: true,
	});

	useEffect(() => {
		if (blankData) {
			dispatch(setStepData(blankData));
		} else {
			dispatch(clearStepData());
		}
	}, [blankData, dispatch]);

	const handleStepComplete = async (stepOneData: StepOneValidationSchema) => {
		const formattedData: Partial<Blank> = {
			...stepOneData,
			gender: GENDER_MAPPING[stepOneData.gender] ?? stepOneData.gender,
			spokesman: SPOKSMAN_MAPPING[stepOneData.spokesman] ?? stepOneData.spokesman,
			id: stepOneData.id && stepOneData.id !== 0 ? stepOneData.id : null,
			establishment: { name: stepOneData.establishment as string },
			stepType: stepData && stepData.problems && stepData.problems.length > 0 ? 'STEP_2' : 'STEP_1',
			problems: stepData?.problems || [],
			consent: stepData?.consent || false,
		};

		// Нормализуем данные для корректного сравнения
		const normalizeDataForComparison = (data: any) => {
			const normalized = _.omit(data, [
				'id',
				'goal',
				'goalPlan',
				'problems',
				'stepType',
				'patientEstimates',
				'userId',
				'changeExecution',
				'changeSatisfaction',
			]);

			// Нормализуем establishment - оставляем только name для сравнения
			if (normalized.establishment && typeof normalized.establishment === 'object') {
				normalized.establishment = { name: normalized.establishment.name };
			}

			return normalized;
		};

		const comparedData = normalizeDataForComparison(formattedData);
		const comparedStep = normalizeDataForComparison(stepData);

		if (_.isEqual(comparedData, comparedStep)) {
			console.log('Данные не изменились, отправка на сервер не требуется.');
			setActiveStep((prevStep) => prevStep + 1);
			return;
		}
		console.log('formattedData после нормализации:', comparedData);
		console.log('stepData после нормализации:', comparedStep);
		console.log('Результат сравнения:', _.isEqual(comparedData, comparedStep));
		console.log(isDataSubmitted);

		try {
			console.log('Данные изменились, отправка на сервер...', formattedData);
			console.log('Текущий пользователь при создании:', currentUser);
			console.log('Токен при создании:', localStorage.getItem('userToken'));
			const result = await upsertBlank(formattedData).unwrap();

			if (result && typeof result === 'number') {
				const blankId: Partial<Blank> = { id: result };
				dispatch(setStepData({ ...formattedData, ...blankId }));
				dispatch(markDataAsSubmitted());
				setLocalClientId(result);
				console.log(
					`✅ Бланк создан с ID: ${result}, пользователь: ${currentUser?.firstName} ${currentUser?.lastName} (ID: ${currentUser?.id})`,
				);
				navigate(`/new-blank/${result}`);
				setActiveStep((prevStep) => prevStep + 1);
				console.log(stepData);
			}
		} catch (err: any) {
			console.error('Ошибка при сохранении данных бланка:', err);

			if (err.data.message.includes('already exists') && err.status === 404) {
				toast.error('Такой email уже существует, введите другой');
			} else {
				toast.error('Не удалось создать бланк');
			}
		}
	};

	const handleStepTwoComplete = async (stepTwoData: StepTwoInfo) => {
		const combinedData: Partial<Blank> = {
			...stepData,

			problems: stepTwoData.problems.map((problem, index) => {
				const existingProblem = stepData.problems?.[index];

				return {
					...problem,

					id: existingProblem?.id || null,
				};
			}),

			stepType: 'STEP_2',
		};

		const omitProblemFieldsForComparison = (problem: any) =>
			_.omit(problem, ['estimate', 'id', 'execution', 'satisfaction']);

		const sanitizedCombinedDataForComparison = {
			..._.omit(combinedData, ['stepType']),
			problems: combinedData.problems?.map(omitProblemFieldsForComparison) || [],
		};

		const sanitizedLocalStorageDataForComparison = {
			..._.omit(stepData, ['stepType']),
			problems: stepData?.problems?.map(omitProblemFieldsForComparison) || [],
		};

		if (_.isEqual(sanitizedCombinedDataForComparison, sanitizedLocalStorageDataForComparison)) {
			console.log('Данные не изменились, отправка на сервер не требуется.');
			setActiveStep((prevStep) => prevStep + 1);
			return;
		}

		console.log('sanitizedCombinedDataForComparison после нормализации:', sanitizedCombinedDataForComparison);
		console.log(
			'sanitizedLocalStorageDataForComparison после нормализации:',
			sanitizedLocalStorageDataForComparison,
		);
		console.log(
			'Результат сравнения:',
			_.isEqual(sanitizedCombinedDataForComparison, sanitizedLocalStorageDataForComparison),
		);
		console.log(isDataSubmitted);

		try {
			console.log('Данные изменились, отправка на сервер...', combinedData);
			const result = await upsertBlank(combinedData).unwrap();

			if (result) {
				dispatch(setStepData(combinedData));
				dispatch(markDataAsSubmitted());
				navigate(`/new-blank/${result}`);
				setActiveStep((prevStep) => prevStep + 1);
				refetch();
			}
		} catch (error) {
			console.error('Ошибка при сохранении данных второго шага:', error);
		}
	};

	const handleStepThreeComplete = async (stepThreeData: StepThreeInfo) => {
		if (!isSuccess || !blankData || !blankData.problems) {
			console.log('Данные ещё не загружены или отсутствуют проблемы');
			return;
		}

		// Находим patientEstimate с самой поздней датой (при равных датах - с большим ID)
		const latestPatientEstimate = getLatestByDateAndId(blankData?.patientEstimates);
		const existingPatientEstimateId = latestPatientEstimate?.id ?? null;

		const patientEstimate: PatientEstimate = {
			id: existingPatientEstimateId,
			date: new Date().toISOString().split('T')[0],
			goal: stepThreeData.goal,
			goalPlan: stepThreeData.goalPlan,
			scoreResult: stepThreeData.scoreResult || '',
		};

		const serverProblems = blankData?.problems || [];

		const topFiveServerProblems = serverProblems
			.slice()
			.sort((a, b) => b.importance - a.importance)
			.slice(0, 5);

		const combinedData: Partial<Blank> = {
			...stepData,

			problems: topFiveServerProblems.map((serverProblem: Problem, index: number) => {
				return {
					...serverProblem,

					estimate: [
						{
							// Находим estimate с самой поздней датой (при равных датах - с большим ID)
							...getLatestByDateAndId(serverProblem.estimate),
							execution: stepThreeData.problems[index].execution || '',
							satisfaction: stepThreeData.problems[index].satisfaction || '',
							prefix: stepThreeData?.problems?.[index]?.prefix?.[0] ?? '',
							domainCode: stepThreeData?.problems?.[index]?.domainCode?.join('') ?? '',
						},
					],
					shortTermGoals:
						serverProblem.shortTermGoals?.map((goal, goalIndex) => ({
							...goal,
							resultScore:
								stepThreeData.problems[index]?.shortTermGoals?.[goalIndex]?.resultScore ||
								goal.resultScore ||
								'',
						})) || [],
				};
			}),
			patientEstimates: [patientEstimate],
			stepType: 'STEP_3',
		};

		const comparedData = _.omit(combinedData, ['id']);
		const comparedStep = _.omit(stepData, ['id']);

		if (_.isEqual(comparedData, comparedStep) && isDataSubmitted) {
			console.log('Данные не изменились, отправка на сервер не требуется.');
			setActiveStep((prevStep) => prevStep + 1);
			return;
		}

		try {
			console.log('Данные изменились, отправка на сервер...', combinedData);
			const result = await upsertBlank(combinedData).unwrap();
			console.log('Данные сохранились');
			if (result) {
				dispatch(setStepData(combinedData));
				dispatch(markDataAsSubmitted());
				navigate(`/`);
			}
		} catch (error) {
			console.error('Ошибка при сохранении данных третьего шага:', error);
		}
	};

	const handleBack = () => {
		setActiveStep((prevActiveStep) => prevActiveStep - 1);
	};

	const getStepContent = () => {
		switch (activeStep) {
			case 0:
				return <StepOneForm active={activeStep} onStepComplete={handleStepComplete} isMobile={isMobile} />;
			case 1:
				return (
					<StepTwoForm
						active={activeStep}
						prevStep={handleBack}
						onStepComplete={(stepTwoData) => handleStepTwoComplete(stepTwoData)}
						isMobile={isMobile}
					/>
				);
			case 2:
				return (
					<StepThreeForm
						active={activeStep}
						prevStep={handleBack}
						onStepComplete={(stepThreeData: StepThreeInfo) => handleStepThreeComplete(stepThreeData)}
						isMobile={isMobile}
					/>
				);
			default:
				return null;
		}
	};

	const handleClick = useCallback(
		(e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>) => {
			e.preventDefault();
			navigate(routes.blank.path);
			dispatch(clearStepData());
			localStorage.removeItem('blankState');
		},
		[navigate, dispatch],
	);

	return (
		<>
			<Modal open={open} className={styles.modal}>
				<div className={styles.modal__title}>
					<h3>{RETURN_QUESTION}</h3>
					<Xmark onClick={() => setOpen(false)} cursor="pointer" />
				</div>
				<div className={styles.modal__content}>{RETURN_TEXT}</div>
				<div className={styles.modal__btns}>
					<Button onClick={() => setOpen(false)} size="xl" view="flat" className={styles.modal__btns__stay}>
						{RETURN_STAY}
					</Button>
					<Button onClick={handleClick} size="xl" className={styles.modal__btns__return}>
						{RETURN_BTN}
					</Button>
				</div>
			</Modal>

			{activeStep === 0 ? (
				<>
					{isMobile ? (
						<div className={styles.title_block_mobile}>
							<Button size="l" view="outlined" onClick={() => setOpen(true)}>
								<Icon data={ArrowLeft} size={16} />
							</Button>

							<h2>{TITLE}</h2>
						</div>
					) : (
						<>
							<Button
								size="l"
								view="outlined"
								className={styles.return_btn}
								onClick={() => setOpen(true)}
							>
								<Icon data={ArrowLeft} size={16} />
								Назад к списку
							</Button>
							<h2>{TITLE}</h2>
						</>
					)}
				</>
			) : (
				<>
					{isMobile ? (
						<div className={styles.title_block_mobile}>
							<Button size="l" view="outlined" onClick={() => setOpen(true)}>
								<Icon data={ArrowLeft} size={16} />
							</Button>

							<h2>{stepData.lastName + ' ' + stepData.firstName + ' ' + stepData.middleName}</h2>
						</div>
					) : (
						<>
							<Button
								size="l"
								view="outlined"
								className={styles.return_btn}
								onClick={() => setOpen(true)}
							>
								<Icon data={ArrowLeft} size={16} />
								Назад к списку
							</Button>

							<h2>{stepData.lastName + ' ' + stepData.firstName + ' ' + stepData.middleName}</h2>
						</>
					)}
				</>
			)}

			<Stepper activeStep={activeStep} nonLinear className={styles.stepper}>
				{steps.map((label, index) => {
					return (
						<Step key={label} className={activeStep === index ? 'step-active' : ''}>
							<StepLabel>{label}</StepLabel>
						</Step>
					);
				})}
			</Stepper>

			<div className={styles.container}>{getStepContent()}</div>
		</>
	);
};
